import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Trans, withI18n } from '@lingui/react';
import TransHTML from "../transHTML";
import { t } from '@lingui/macro';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useRouter } from 'next/router';
import { getUTMParams } from '~/components/UTMParams';
import ClassicInput from '~/components/inputs/ClassicInput';
import PhoneInput from '~/components/inputs/PhoneInput';
import { sendGoogleScript } from '~/components/contact-form/contact-form';
import { doRedirectToRegistration } from '~/components/parent-form';
import LoadingSplash from '~/components/loadingSplash/LoadingSplash';
import { LangContext } from '~/components/../providers/LangProvider';
import { tabletBreakpoint } from '~/styles/common';
import { WelcomeWrapper } from '~/styles/welcome';
import { YellowButton } from '~/styles/buttons';

const initValues = {
    name: undefined,
    email: undefined,
    phone: undefined,
};

function LeadFormOcellot({ googleScript, i18n }) {
    const ctx = useContext(LangContext);
    const router = useRouter();
    const googleSheetName = 'responses';
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [formSent, setFormSent] = useState(false);
    const [startValidationOnChange, setStartValidationOnChange] = useState(false);
    const [phoneValid, setPhoneValid] = useState(false);
    const [nameValid, setNameValid] = useState(false);
    const [emailValid, setEmailValid] = useState(false);
    const [disabled, setDisabled] = useState(true);

    const getValidation = (i18n = null) => Yup.object().shape({
        name: Yup.string().required(i18n?._('cz.ocellot.sign_up.form.name.required')),
        email: Yup.string()
            .required(i18n?._(t`cz.ocellot.sign_up.form.email.required`))
            .email(i18n?._(t`cz.ocellot.sign_up.form.email.validFormat`)),
    });

    const handleSubmit = async (values) => {
        setLoading(true);

        const formData = {
            name: values.name,
            email: values.email,
            phone: values.phone.withPrefix,
            language: ctx.state.lang,
        };

        const formDataNameOrder = { ...formData, ...getUTMParams(), URL: `${window.location.hostname}/${ctx.state.lang}${router.pathname}` };

        const dataToSend = {
            ...formDataNameOrder,
            formGoogleSheetName: googleSheetName,
            formDataNameOrder: JSON.stringify(formDataNameOrder),
        };

        const response = await sendGoogleScript(
            googleScript,
            dataToSend,
            'successSendContactForm',
        );

        setFormSent(response.sent);
        setLoading(false);
        setErrorMessage(response.error);

        history.pushState(null, '', `${window.location.pathname}?form_sent=true&form_name=intro_form`);
        if (response.sent) {
            doRedirectToRegistration(router, formData);
        }
    };

    useEffect(() => {
        if (phoneValid && nameValid && emailValid) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [phoneValid, nameValid, emailValid]);

    useEffect(() => {
        setDisabled(true);
    }, [formSent]);

    return (
        <LeadFromWelcomeWrapper
            id="welcome"
            className={'mt-4 mt-lg-0 smaller'}>
            {!formSent && (
                <LeadFormWrapper>
                    <FormWrapper>
                        <LeadFormHeader>
                            <Trans id="cz.ocellot.join_us.lead_form.heading" />
                        </LeadFormHeader>
                        <LoadingSplash
                            isLoading={loading}
                            overlay={false}>
                            <Formik
                                validationSchema={getValidation}
                                validateOnChange={startValidationOnChange}
                                validateOnBlur={startValidationOnChange}
                                validate={(values) => {
                                    setStartValidationOnChange(true);

                                    const errors = {};
                                    if (!phoneValid) {
                                        errors.phone = i18n._(
                                            t`contactForm.phone.required`,
                                        );
                                    }
                                    return errors;
                                }}
                                initialValues={initValues}
                                onSubmit={(values) => {
                                    handleSubmit(values);
                                }}
                            >
                                {({ errors, touched }) => (
                                    <FormikForm>
                                        <ClassicInput
                                            name={'name'}
                                            touched={touched}
                                            errors={errors}
                                            label={i18n._('contactForm.name.label')}
                                        />
                                        <ClassicInput
                                            name={'email'}
                                            touched={touched}
                                            errors={errors}
                                            label={i18n._('contactForm.email.label')}
                                            setNameValid={setNameValid}
                                            setEmailValid={setEmailValid}
                                            formSent={formSent}
                                        />
                                        <PhoneInput
                                            name={'phone'}
                                            touched={touched}
                                            errors={errors}
                                            setPhoneValid={setPhoneValid}
                                            phoneErrorMessage={i18n._(
                                                'contactForm.phone.required'
                                            )}
                                            label={i18n._('contactForm.phone.label')}
                                        />
                                        <CustomSubmitButton type="submit">
                                            <Trans id="cz.ocellot.join_us.lead_form.submit" />
                                        </CustomSubmitButton>
                                    </FormikForm>
                                )}
                            </Formik>
                        </LoadingSplash>
                    </FormWrapper>
                    <InformationWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`cz.ocellot.join_us.lead_form.point1`} />
                            </PointText>
                        </PointWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`cz.ocellot.join_us.lead_form.point2`} />
                            </PointText>
                        </PointWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`cz.ocellot.join_us.lead_form.point3`} />
                            </PointText>
                        </PointWrapper>
                        <PointWrapper>
                            <PointImg src="/static/images/new_web/green_tick.svg" />
                            <PointText>
                                <TransHTML
                                    id={t`cz.ocellot.join_us.lead_form.point4`} />
                            </PointText>
                        </PointWrapper>
                    </InformationWrapper>
                </LeadFormWrapper>
            )}
        </LeadFromWelcomeWrapper>
    );
}

export default withI18n()(LeadFormOcellot);

const LeadFormWrapper = styled.div`
    max-width: 860px;
    width: 100%;
    margin: 0 auto;
    background-color: white;
    padding: 56px;
    box-shadow: 0 0 33px #e9edec;
    border-radius: 8px;
    display: flex;
    flex-direction: row;
    justify-content: space-around;

    @media (max-width: ${tabletBreakpoint}) {
        flex-direction: column;
        padding: 15px;
        align-items: center;
    }

    .MuiFormControl-root {
        width: 100%;
        height: 55px;
    }
`;

const FormWrapper = styled.div`
    margin-right: 56px;
    max-width: 375px;
    width: 100%;

    @media (max-width: ${tabletBreakpoint}) {
        margin-right: 0;
    }
`;

const InformationWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 70px;
    min-width: 320px;
`;

const LeadFromWelcomeWrapper = styled(WelcomeWrapper)`
    padding-bottom: 87px;
    padding-top: 75px;
    height: 100% !important;
    background-image: none !important;

    @media (max-width: ${tabletBreakpoint}) {
        height: 100% !important;
    }
`;

const LeadFormHeader = styled.div`
    font-style: normal;
    font-weight: bold;
    font-size: 35px;
    line-height: 56px;
    letter-spacing: -0.25px;
    color: #0cc8a8;
    margin-bottom: 16px;
    text-align: center;
`;

const PointWrapper = styled.div`
    display: flex;
    margin-bottom: 50px;

    @media (max-width: 400px) {
        margin-left: 30px;
    }
`;

const PointImg = styled.img`
    margin-right: 26px;
    width: 32px;
`;

const PointText = styled.div`
    font-style: normal;
    font-size: 24px;
    line-height: 36px;
    letter-spacing: -0.25px;
    color: #2d2d2d;

    @media (max-width: ${tabletBreakpoint}) {
        font-size: 18px;
    }
`;

const FormikForm = styled(Form)`
    width: 100%;
`;

const CustomSubmitButton = styled(YellowButton)`
    width: 100%;
    margin-top: 16px;
`;
